/** Cache des buffers décodés (évite de re-fetch à chaque trappe). */
const bufferCache = new Map();
const loadingPromises = new Map();

let sharedAudioCtx = null;
let gameSampleMixHook = null;
/** Source longue (chrono) — stoppable entre deux questions. */
let activeStoppableSource = null;

export function stopActiveStoppableSample() {
  if (!activeStoppableSource) return;
  try {
    activeStoppableSource.stop(0);
  } catch {
    /* ignore */
  }
  activeStoppableSource = null;
}

export function getSharedAudioContext() {
  return getAudioContext();
}

function getAudioContext() {
  if (!sharedAudioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    sharedAudioCtx = new Ctor();
  }
  return sharedAudioCtx;
}

/** Débloque l'AudioContext (politique autoplay des navigateurs). */
export function unlockAudioContext() {
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve(false);
  if (ctx.state === "running") return Promise.resolve(true);
  return ctx.resume().then(() => ctx.state === "running").catch(() => false);
}

/**
 * À appeler dans un geste utilisateur (Activer caméra / Lancer le jeu).
 * Joue un silence pour « ouvrir » la sortie audio mobile — les questions
 * pourront ensuite se lire toutes seules via Web Audio.
 */
export async function primeAudioForPlayback() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  try {
    if (ctx.state === "suspended") {
      await ctx.resume();
    }
    const buffer = ctx.createBuffer(1, 1, ctx.sampleRate || 44100);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start(0);
    // HTML Audio silence (certains Android)
    const el = new Audio(
      "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=",
    );
    el.volume = 0.01;
    el.playsInline = true;
    await el.play().catch(() => {});
    el.pause();
    return ctx.state === "running";
  } catch {
    return unlockAudioContext();
  }
}

async function loadBuffer(url) {
  if (bufferCache.has(url)) return bufferCache.get(url);
  if (loadingPromises.has(url)) return loadingPromises.get(url);

  const promise = (async () => {
    const ctx = getAudioContext();
    if (!ctx) throw new Error("Web Audio indisponible");
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Audio fetch failed: ${url}`);
    const buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    bufferCache.set(url, buffer);
    return buffer;
  })();

  loadingPromises.set(url, promise);
  try {
    return await promise;
  } finally {
    loadingPromises.delete(url);
  }
}

/** Précharge les samples en mémoire (appeler au montage de Play). */
export async function preloadAudioSamples(urls) {
  await unlockAudioContext();
  const unique = [...new Set(urls.filter(Boolean))];
  await Promise.all(unique.map((url) => loadBuffer(url).catch(() => null)));
}

/** Enregistrement composite : branche les samples Web Audio dans le mix vidéo. */
export function registerGameSampleMixHook(hook) {
  gameSampleMixHook = typeof hook === "function" ? hook : null;
}

function isMixRoutingActive() {
  return typeof gameSampleMixHook === "function";
}

/** Haut-parleurs + piste enregistrement (même gain). */
function routeGameSample(ctx, source, gain) {
  const speakerGain = ctx.createGain();
  speakerGain.gain.value = gain;
  source.connect(speakerGain);
  speakerGain.connect(ctx.destination);

  if (isMixRoutingActive()) {
    const recordGain = ctx.createGain();
    recordGain.gain.value = gain;
    source.connect(recordGain);
    gameSampleMixHook(recordGain);
  }
}

async function playHtmlFallback(url, volume = 1, { signal } = {}) {
  const clip = new Audio();
  clip.preload = "auto";
  clip.playsInline = true;
  clip.setAttribute("playsinline", "true");
  clip.setAttribute("webkit-playsinline", "true");
  clip.volume = Math.min(1, Math.max(0, volume));
  clip.src = url;

  await new Promise((resolve, reject) => {
    let settled = false;
    const finish = (err) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener?.("abort", onAbort);
      if (err) reject(err);
      else resolve();
    };
    const onAbort = () => {
      try {
        clip.pause();
      } catch {
        /* ignore */
      }
      finish(new DOMException("Aborted", "AbortError"));
    };
    clip.onended = () => finish();
    clip.onerror = () => finish(new Error("HTML audio error"));
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener("abort", onAbort, { once: true });
    }
    clip.play().then(() => {}).catch((err) => finish(err));
  });
}

/**
 * Joue un sample court (push, Open, etc.) via Web Audio avec gain > 1.
 * Repli HTMLAudioElement si Web Audio indisponible ou en échec.
 */
export async function playGameSample(url, gain = 1) {
  const ctx = getAudioContext();
  if (!ctx) {
    await playHtmlFallback(url, gain);
    return;
  }

  await unlockAudioContext();

  try {
    const buffer = await loadBuffer(url);
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    routeGameSample(ctx, source, gain);
    source.start(0);
  } catch {
    await playHtmlFallback(url, gain);
  }
}

/**
 * Joue un ArrayBuffer : Web Audio d'abord, sinon <audio> HTML.
 * Sur Android, appeler idéalement depuis un geste utilisateur.
 * @param {{ signal?: AbortSignal, mimeType?: string }} [options]
 */
export async function playAudioArrayBuffer(arrayBuffer, gain = 1, options = {}) {
  const { signal, mimeType = "audio/mpeg" } = options;
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  const playViaHtml = async () => {
    const blob = new Blob([arrayBuffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    try {
      await playHtmlFallback(url, Math.min(1, gain), { signal });
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const ctx = getAudioContext();
  if (!ctx) {
    await playViaHtml();
    return;
  }

  await unlockAudioContext();
  if (ctx.state === "suspended") {
    await ctx.resume().catch(() => {});
  }
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  try {
    // Safari / Chrome : copie détachée obligatoire pour decodeAudioData
    const copy = arrayBuffer.slice(0);
    const buffer = await ctx.decodeAudioData(copy);
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

    await new Promise((resolve, reject) => {
      let settled = false;
      let source;
      const finish = (err) => {
        if (settled) return;
        settled = true;
        signal?.removeEventListener?.("abort", onAbort);
        if (err) reject(err);
        else resolve();
      };
      const onAbort = () => {
        try {
          source?.stop(0);
        } catch {
          /* ignore */
        }
        finish(new DOMException("Aborted", "AbortError"));
      };
      try {
        source = ctx.createBufferSource();
        source.buffer = buffer;
        routeGameSample(ctx, source, gain);
        source.onended = () => finish();
        if (signal) signal.addEventListener("abort", onAbort, { once: true });
        source.start(0);
      } catch (err) {
        finish(err);
      }
    });
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    // decode / Web Audio KO → HTML (souvent OK sur Android avec le bon mime)
    await playViaHtml();
  }
}

/** true sur téléphone / tablette (autoplay audio souvent bloqué). */
export function isCoarsePointerDevice() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia?.("(pointer: coarse)")?.matches) return true;
  } catch {
    /* ignore */
  }
  return "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 1;
}

/** Comme playGameSample, résout à la fin de la lecture. */
export async function playGameSampleWithEnd(url, gain = 1, { stoppable = false } = {}) {
  const ctx = getAudioContext();
  if (!ctx) {
    await playHtmlFallback(url, gain);
    return;
  }

  await unlockAudioContext();

  try {
    const buffer = await loadBuffer(url);
    if (ctx.state === "suspended") {
      await ctx.resume();
    }

    await new Promise((resolve) => {
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      routeGameSample(ctx, source, gain);
      const finish = () => {
        if (stoppable && activeStoppableSource === source) {
          activeStoppableSource = null;
        }
        resolve();
      };
      source.onended = finish;
      if (stoppable) {
        stopActiveStoppableSample();
        activeStoppableSource = source;
      }
      source.start(0);
    });
  } catch {
    await playHtmlFallback(url, gain);
  }
}

/** @deprecated Utiliser playGameSample */
export async function playBoostedSample(url, gain = 2.5) {
  return playGameSample(url, gain);
}

export function playClonedSample(audioEl) {
  if (!audioEl) return;
  const clip = audioEl.cloneNode(true);
  clip.volume = 1;
  clip.play().catch(() => {});
}

/** Attend que l'élément <audio> soit prêt (chrono, valid, etc.). */
export function waitAudioElementReady(audioEl) {
  if (!audioEl) return Promise.resolve(false);
  if (audioEl.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const finish = (ok) => {
      audioEl.removeEventListener("canplaythrough", onReady);
      audioEl.removeEventListener("error", onError);
      resolve(ok);
    };
    const onReady = () => finish(audioEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA);
    const onError = () => finish(false);
    audioEl.addEventListener("canplaythrough", onReady, { once: true });
    audioEl.addEventListener("error", onError, { once: true });
    audioEl.load();
  });
}
